import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { visibleActions } from '@/lib/navConfig';

export const HubQuickActions = ({ user }) => {
  const { t } = useTranslation();
  return (
    <Card data-testid="hub-quick-actions">
      <CardHeader className="pb-3"><CardTitle className="text-base md:text-lg">{t('hub.quickActions')}</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-2 gap-2">
        {visibleActions(user).map((a) => {
          const Icon = a.icon;
          return (
            <Button key={a.key} variant="outline" asChild className="h-auto py-3 justify-start gap-2 hover:border-orange-400 hover:text-orange-600" data-testid={`hub-quick-${a.key}`}>
              <Link to={a.to}><Icon className="h-4 w-4 text-orange-500" /><span className="text-xs sm:text-sm">{t(a.labelKey)}</span></Link>
            </Button>
          );
        })}
      </CardContent>
    </Card>
  );
};

const statusTone = (s) => (s === 'completed' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
  : s === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
  : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400');

export const HubRecent = ({ items }) => {
  const { t } = useTranslation();
  return (
    <Card data-testid="hub-recent">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base md:text-lg">
          <ArrowLeftRight className="h-4 w-4 text-emerald-500" /> {t('hub.recentTitle')}
        </CardTitle>
        <Button variant="ghost" size="sm" asChild data-testid="hub-recent-view-all">
          <Link to="/transactions" className="gap-1 text-xs">{t('hub.viewAll')} <ArrowRight className="h-3 w-3" /></Link>
        </Button>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="hub-recent-empty">{t('hub.recentEmpty')}</p>
        ) : (
          <ul className="divide-y">
            {items.map((tx) => (
              <li key={tx.transaction_id} className="flex items-center justify-between gap-3 py-2.5 text-sm" data-testid={`hub-recent-${tx.transaction_id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{tx.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {tx.order_number ? `${tx.order_number} · ` : ''}
                    {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''} · {tx.role === 'seller' ? t('hub.asSeller') : t('hub.asBuyer')}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {tx.price_EUR != null && <span className="font-semibold tabular-nums">€{Number(tx.price_EUR).toLocaleString('de-DE', { minimumFractionDigits: 2 })}</span>}
                  <Badge className={`${statusTone(tx.status)} text-[10px]`}>{t(`status.${tx.status}`, tx.status)}</Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
